import { isEmpty, isNil } from '@/helpers/type-guards'
import { Currency } from '@/models/common'
import i18n from '@/translations/i18n'

export const getTotal = <D extends object>(arr: Array<D>, key: string) =>
  arr.reduce((sum, row) => {
    // Check if the value is null or undefined, use 0 if it is, otherwise use the value
    const value = isEmpty(row[key]) || isNil(row[key]) ? 0 : row[key]
    return value + sum
  }, 0)

interface FormatOptions {
  withSign?: boolean
  fractionDigits?: number
  currency?: Currency
}

export const formatNumber = (value: number, options: FormatOptions = {}): string => {
  if (isNil(value) || isEmpty(value)) {
    return '-'
  }

  const { fractionDigits = 2 } = options
  const isWholeNumber = value % 1 === 0

  const formatter = new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: isWholeNumber ? 0 : fractionDigits,
    minimumFractionDigits: isWholeNumber ? 0 : fractionDigits,
  })

  return formatter.format(value).replace(/\./g, ' ')
}

export const formatPercent = (rate: number, options: FormatOptions = {}): string => {
  const { fractionDigits = 2, withSign = true } = options

  if (isNil(rate) || isEmpty(rate)) {
    return withSign ? '- %' : ''
  }
  const isWholeNumber = rate % 1 === 0
  const formatter = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: isWholeNumber ? 0 : fractionDigits,
  })

  return `${formatter.format(rate)}${withSign ? ' %' : ''}`
}

export const getCurrencySymbol = (currency: Currency) => {
  const currencySymbolMap = {
    [Currency.EUR]: '€ ', // Example locale for Euro
    [Currency.PLN]: 'zł ', // Polish locale for Zloty
    // Add more currencies and their respective locales here
  }

  return currencySymbolMap[currency] || '€ '
}

export const formatCurrency = (value: number, options: FormatOptions = {}): string => {
  const { fractionDigits = 2, currency = Currency.EUR } = options
  const effectiveCurrency = currency || Currency.EUR
  const currencySymbol = getCurrencySymbol(effectiveCurrency)

  if (isNil(value) || isEmpty(value)) {
    return `${currencySymbol} -`
  }

  const sign = value < 0 ? '-' : ''
  const absoluteValue = Math.abs(value)
  const isWholeNumber = absoluteValue % 1 === 0

  // Format the absolute value using 'en-US' locale
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: effectiveCurrency,
    maximumFractionDigits: isWholeNumber ? 0 : fractionDigits,
  })

  // Extracting just the numeric part of the formatted value
  const formattedValue = formatter.format(absoluteValue).replace(/[^\d.,]/g, '')

  return `${sign} ${currencySymbol}${formattedValue}`
}

export const formatDays = (days: number, options: FormatOptions = {}): string => {
  if (isNil(days) || isEmpty(days)) {
    return '-'
  }

  const { fractionDigits = 0 } = options

  return `${formatNumber(days, { fractionDigits })} ${i18n.t('global.days_suffix')}`
}

export const formatMonths = (months: number): string => {
  if (isNil(months) || isEmpty(months)) {
    return '-'
  }

  return `${formatNumber(months, { fractionDigits: 0 })} ${i18n.t('global.months_suffix')}`
}

export const roundNumber = (num: number, scale = 2): number => {
  if (!num.toString().includes('e')) {
    return +(Math.round(+`${num}e+${scale}`) + 'e-' + scale)
  } else {
    const arr = ('' + num).split('e')
    let sig = ''
    if (+arr[1] + scale > 0) {
      sig = '+'
    }
    return +(Math.round(+`${arr[0]}e${sig}${+arr[1] + scale}`) + 'e-' + scale)
  }
}

export const getRoundedTotal = <D extends object>(arr: Array<D>, key: string) => {
  const total = getTotal(arr, key)

  return roundNumber(total)
}

export const getSuggestedAmount = (monthlyTurnover: number, paymentTerm: number) => monthlyTurnover * (paymentTerm / 30)

export const isNegativeNumber = (value: unknown): boolean => typeof value === 'number' && value < 0
